import moment from 'moment';
import {
  EVENT_MONTHLY_BY_INTERVAL,
  EVENT_MONTHLY_BY_MONTHS,
  EVENT_RECURRENCE_DAILY,
  EVENT_RECURRENCE_MONTHLY,
  EVENT_RECURRENCE_WEEKLY,
  EVENT_RECURRENCE_YEARLY,
  EVENT_REPEAT_ON_ORDINAL_TO_VALUE,
  EVENT_REPEAT_ON_VALUE_TO_ORDINAL,
} from '../constants/recurrence.constants';
import {
  ScheduleRecurrenceMode,
  ScheduleRecurrencePayload,
} from '../models/request-schedule.model';

export type RecurrenceTranslateFn = (
  key: string,
  params?: Record<string, string | number>
) => string;

/** @deprecated Prefer RecurrenceTranslateFn */
export type EventTranslateFn = RecurrenceTranslateFn;

/** Day-of-month number = specific; ordinal key string = relative. */
export type RepeatOnOptionValue = number | string;

/** Recurrence fields come back either as translated options or as raw values. */
export function resolveRecurrenceOptionValue(
  option: { value?: string | null } | string | null | undefined
): string | undefined {
  if (typeof option === 'string') {
    return option || undefined;
  }

  return option?.value ?? undefined;
}

export interface RecurrenceFormValue {
  type?: string | null;
  interval?: number | null;
  days_of_week?: Array<string | number> | null;
  by?: string | null;
  months?: number[] | null;
  repeatOnByDayOfMonth?: RepeatOnOptionValue | null;
}

const ORDINAL_MAP: Record<number, { key: string; value: number }> = {
  1: { key: 'first', value: 1 },
  2: { key: 'second', value: 2 },
  3: { key: 'third', value: 3 },
  4: { key: 'fourth', value: 4 },
  5: { key: 'last', value: -1 },
};

const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

/** Monday = 1 … Sunday = 7 */
const WEEKDAY_NAME_TO_NUMBER: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

export function getNthWeekdayInMonth(date: Date): {
  weekIndex: number;
  ordinalKey: string;
  ordinalValue: number;
  dayKey: string;
  monthKey: string;
} {
  const day = date.getDate();
  const weekIndex = Math.ceil(day / 7);
  const ordinal = ORDINAL_MAP[weekIndex] ?? ORDINAL_MAP[1];
  return {
    weekIndex,
    ordinalKey: ordinal.key,
    ordinalValue: ordinal.value,
    dayKey: DAY_KEYS[date.getDay()],
    monthKey: MONTH_KEYS[date.getMonth()],
  };
}

function buildWeekdayRepeatOnOptions(
  startDate: Date,
  translate: RecurrenceTranslateFn,
  buildLabel: (ordinalKey: string, dayKey: string, monthKey: string) => string
): { label: string; value: string }[] {
  const { weekIndex, ordinalKey, dayKey, monthKey } =
    getNthWeekdayInMonth(startDate);

  const options: { label: string; value: string }[] = [
    {
      label: buildLabel(ordinalKey, dayKey, monthKey),
      value: ordinalKey,
    },
  ];

  // The 4th occurrence may also be the last in the month.
  if (weekIndex === 4) {
    options.push({
      label: buildLabel('last', dayKey, monthKey),
      value: 'last',
    });
  }

  return options;
}

/** Specific = day-of-month number; relative = ordinal key string. */
export function isSpecificRepeatOn(
  repeatOn: RepeatOnOptionValue | null | undefined
): boolean {
  return typeof repeatOn === 'number';
}

export function resolveRelativeOption(
  repeatOn: RepeatOnOptionValue | null | undefined
): number {
  if (typeof repeatOn === 'string') {
    return EVENT_REPEAT_ON_ORDINAL_TO_VALUE[repeatOn] ?? 1;
  }

  return 1;
}

export function mapRepeatOnFromApi(
  mode?: string | null,
  relativeOption?: number | null,
  daysOfMonth?: number[] | null
): RepeatOnOptionValue | null {
  if (mode === 'relative' && relativeOption != null) {
    return EVENT_REPEAT_ON_VALUE_TO_ORDINAL[relativeOption] ?? relativeOption;
  }

  if (mode === 'specific' && daysOfMonth?.[0] != null) {
    return daysOfMonth[0];
  }

  if (relativeOption != null) {
    return EVENT_REPEAT_ON_VALUE_TO_ORDINAL[relativeOption] ?? relativeOption;
  }

  return daysOfMonth?.[0] ?? null;
}

export function formatRepeatOnDisplay(
  type: string | null | undefined,
  startDate: Date,
  mode: string | null | undefined,
  relativeOption: number | null | undefined,
  daysOfMonth: number[] | null | undefined,
  translate: RecurrenceTranslateFn
): string | null {
  const repeatOnValue = mapRepeatOnFromApi(mode, relativeOption, daysOfMonth);
  if (repeatOnValue == null) {
    return null;
  }

  const options =
    type === EVENT_RECURRENCE_MONTHLY
      ? buildMonthlyRepeatOnOptions(startDate, translate)
      : type === EVENT_RECURRENCE_YEARLY
        ? buildYearlyRepeatOnOptions(startDate, translate)
        : [];

  return options.find(option => option.value === repeatOnValue)?.label ?? null;
}

export function buildMonthlyRepeatOnOptions(
  startDate: Date,
  translate: RecurrenceTranslateFn
): {
  label: string;
  value: RepeatOnOptionValue;
}[] {
  const dayOfMonth = startDate.getDate();

  return [
    {
      label: translate('schedule_workflow_repeat_on_monthly_day_option', {
        day: dayOfMonth,
      }),
      value: dayOfMonth,
    },
    ...buildWeekdayRepeatOnOptions(startDate, translate, (ordinalKey, dayKey) =>
      translate('schedule_workflow_repeat_on_monthly_weekday_option', {
        ordinal: translate(`schedule_workflow_ordinal_${ordinalKey}_label`),
        dayName: translate(`schedule_workflow_week_day_${dayKey}_label`),
      })
    ),
  ];
}

export function buildYearlyRepeatOnOptions(
  startDate: Date,
  translate: RecurrenceTranslateFn
): {
  label: string;
  value: RepeatOnOptionValue;
}[] {
  const dayOfMonth = startDate.getDate();
  const { monthKey } = getNthWeekdayInMonth(startDate);
  const monthName = translate(`schedule_workflow_month_${monthKey}_label`);

  return [
    {
      label: translate('schedule_workflow_repeat_on_yearly_day_option', {
        day: dayOfMonth,
        month: monthName,
      }),
      value: dayOfMonth,
    },
    ...buildWeekdayRepeatOnOptions(startDate, translate, (ordinalKey, dayKey) =>
      translate('schedule_workflow_repeat_on_yearly_weekday_option', {
        ordinal: translate(`schedule_workflow_ordinal_${ordinalKey}_label`),
        dayName: translate(`schedule_workflow_week_day_${dayKey}_label`),
        month: monthName,
      })
    ),
  ];
}

export function parseFormDateToDate(
  value: string | null | undefined
): Date | undefined {
  if (!value) return undefined;
  const dateValue = value.includes(' - (')
    ? value.split(' - (')[0].trim()
    : value.trim();
  const m = moment.utc(dateValue, 'DD-MMM-yyyy hh:mm A', true);
  if (m.isValid()) {
    return m.toDate();
  }
  const fallback = moment(dateValue);
  return fallback.isValid() ? fallback.toDate() : undefined;
}

/** Inclusive unique month numbers between two dates (1–12), in chronological order. */
export function getMonthsBetweenDates(
  startDate: Date,
  endDate: Date
): number[] {
  const start = moment(startDate).startOf('month');
  const end = moment(endDate).startOf('month');
  if (!start.isValid() || !end.isValid()) {
    return [];
  }

  const [from, to] = start.isAfter(end) ? [end, start] : [start, end];
  const months: number[] = [];
  const cursor = from.clone();

  while (cursor.isSameOrBefore(to, 'month')) {
    months.push(cursor.month() + 1);
    cursor.add(1, 'month');
  }

  return months;
}

function buildSpecificPayload(
  type: string,
  interval: number,
  months: number[] | undefined,
  dayOfMonth: number,
  by?: string
): ScheduleRecurrencePayload {
  return {
    type,
    interval,
    mode: ScheduleRecurrenceMode.SPECIFIC,
    days_of_month: [dayOfMonth],
    ...(months !== undefined ? { months } : {}),
    ...(by ? { by } : {}),
  };
}

function buildRelativePayload(
  type: string,
  interval: number,
  months: number[] | undefined,
  relativeOption: number,
  weekdayNumber: number,
  by?: string
): ScheduleRecurrencePayload {
  return {
    type,
    interval,
    mode: ScheduleRecurrenceMode.RELATIVE,
    relative_option: relativeOption,
    days_of_week: [weekdayNumber],
    ...(months !== undefined ? { months } : {}),
    ...(by ? { by } : {}),
  };
}

function buildMonthlyOrYearlyPayload(
  type: typeof EVENT_RECURRENCE_MONTHLY | typeof EVENT_RECURRENCE_YEARLY,
  recurrence: RecurrenceFormValue,
  startDate: Date
): ScheduleRecurrencePayload {
  const repeatOn = recurrence.repeatOnByDayOfMonth;
  const weekdayNumber =
    WEEKDAY_NAME_TO_NUMBER[DAY_KEYS[startDate.getDay()]] ?? 1;

  const isMonthly = type === EVENT_RECURRENCE_MONTHLY;
  const by = isMonthly ? recurrence.by || EVENT_MONTHLY_BY_INTERVAL : undefined;
  const isByMonths = by === EVENT_MONTHLY_BY_MONTHS;
  const interval = isByMonths ? 1 : Number(recurrence.interval || 1);

  let months: number[] | undefined;
  if (isMonthly && isByMonths) {
    months = recurrence.months ?? undefined;
  } else if (!isMonthly) {
    months = [startDate.getMonth() + 1];
  }

  if (isSpecificRepeatOn(repeatOn)) {
    const dayOfMonth =
      typeof repeatOn === 'number' ? repeatOn : startDate.getDate();
    return buildSpecificPayload(type, interval, months, dayOfMonth, by);
  }

  return buildRelativePayload(
    type,
    interval,
    months,
    resolveRelativeOption(repeatOn),
    weekdayNumber,
    by
  );
}

function resolveStartDate(
  startedAt?: string | Date | null
): Date | undefined {
  if (startedAt instanceof Date) {
    return startedAt;
  }

  return parseFormDateToDate(startedAt);
}

/** Week day values may come from the form as names or numbers; the API expects numbers. */
function toWeekDayNumbers(days?: Array<string | number> | null): number[] {
  return (days ?? []).map(day =>
    typeof day === 'number'
      ? day
      : (WEEKDAY_NAME_TO_NUMBER[day.toLowerCase()] ?? Number(day))
  );
}

export function prepareRecurrencePayload(
  recurrence: RecurrenceFormValue,
  startedAt?: string | Date | null
): ScheduleRecurrencePayload {
  const type = recurrence.type || '';
  const interval = Number(recurrence.interval || 1);
  const startDate = resolveStartDate(startedAt);

  if (type === EVENT_RECURRENCE_DAILY) {
    return {
      type: EVENT_RECURRENCE_DAILY,
      interval,
    };
  }

  if (type === EVENT_RECURRENCE_WEEKLY) {
    return {
      type: EVENT_RECURRENCE_WEEKLY,
      interval,
      days_of_week: toWeekDayNumbers(recurrence.days_of_week),
    };
  }

  if (type === EVENT_RECURRENCE_MONTHLY) {
    const by = recurrence.by || EVENT_MONTHLY_BY_INTERVAL;
    const monthlyInterval = by === EVENT_MONTHLY_BY_MONTHS ? 1 : interval;

    if (!startDate) {
      return {
        type: EVENT_RECURRENCE_MONTHLY,
        interval: monthlyInterval,
        by,
        ...(by === EVENT_MONTHLY_BY_MONTHS && recurrence.months
          ? { months: recurrence.months }
          : {}),
      };
    }

    return buildMonthlyOrYearlyPayload(
      EVENT_RECURRENCE_MONTHLY,
      recurrence,
      startDate
    );
  }

  if (type === EVENT_RECURRENCE_YEARLY) {
    if (!startDate) {
      return {
        type: EVENT_RECURRENCE_YEARLY,
        interval,
      };
    }

    return buildMonthlyOrYearlyPayload(
      EVENT_RECURRENCE_YEARLY,
      recurrence,
      startDate
    );
  }

  return {
    type,
    interval,
  };
}
